import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { loginHref, TENANT } from '@/routing/tenant';
import type { ActiveClinic, PublicUser } from '@/types/api';

export type { ActiveClinic };
export type AuthUser = PublicUser;

interface SessionPayload {
  token: string;
  user: AuthUser;
  memberships: ActiveClinic[];
}

interface AuthState {
  token: string | null;
  user: AuthUser | null;
  memberships: ActiveClinic[];
  setSession: (session: SessionPayload) => void;
  setMemberships: (memberships: ActiveClinic[]) => void;
  /**
   * Add or refresh ONE membership without disturbing the others.
   *
   * Creating a clinic and accepting an invitation both used to overwrite the
   * whole list, which was harmless when a user could only ever have one. It is
   * not harmless now: replacing the list makes every other clinic look like it
   * is not theirs, and `RequireAuth` would evict an open tab sitting in one.
   */
  addMembership: (membership: ActiveClinic) => void;
  logout: (opts?: { redirect?: boolean }) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      memberships: [],
      setSession: ({ token, user, memberships }) =>
        set({ token, user, memberships }),
      setMemberships: (memberships) => set({ memberships }),
      addMembership: (membership) =>
        set((state) => ({
          memberships: [...state.memberships.filter((m) => m.id !== membership.id), membership],
        })),
      logout: ({ redirect = false } = {}) => {
        set({ token: null, user: null, memberships: [] });
        // Hard navigation on purpose: guarantees the in-memory React tree,
        // TanStack Query cache, and any other in-flight state are reset.
        // Trade-off: drops the `from` location for redirect-after-login.
        //
        // Back to THIS clinic's login, not the global one: signing out of
        // `/acme` and being dropped somewhere that no longer mentions the
        // clinic reads as an error rather than a sign-out.
        if (redirect && typeof window !== 'undefined') {
          const target = loginHref();
          if (window.location.pathname !== target) window.location.assign(target);
        }
      },
    }),
    {
      name: 'synapse.auth',
      // Persist only the long-lived session bits. `memberships` is server
      // state — caching it across reloads would let a removed user keep
      // clinic-scoped UI access offline. The app refetches memberships via
      // useMyMemberships() on mount of the protected shell.
      partialize: (state) => ({ token: state.token, user: state.user }),
    },
  ),
);

/**
 * Which of the user's clinics this page load is about: the one the URL names.
 *
 * The slug is not a hint here — it is the same value the API scopes every
 * request by (`/api/c/<slug>/...`), so what this returns and what the server
 * enforces cannot disagree. That is what lets `useAuth().permissions` drive
 * `RequirePermission` and the nav without ever offering an action the API will
 * refuse.
 *
 * Null in the global namespace (login, register, the clinic picker, onboarding),
 * where there is no clinic yet and nothing clinic-scoped renders.
 */
export function pickActiveClinic(memberships: ActiveClinic[]): ActiveClinic | null {
  if (TENANT.mode !== 'tenant') return null;
  return memberships.find((m) => m.slug === TENANT.slug) ?? null;
}
